import {Config, HvaModel, HvaResponse,} from '../../@types/hva.js';

export class HvaModalModel {
  private static EMPTY = new HvaModalModel(
    '',
    '',
    '',
    '',
    [],
    0,
  );

  public readonly productName: string;

  public readonly subName: string;

  public readonly variantId: string;

  public readonly maximumSizesSelectedError: boolean;

  private readonly placeHolderText: string;

  private readonly hva: HvaModel[];

  private readonly selectedStoreId: number;

  private readonly selectedSizes: Set<string>;

  private constructor(
    variantId: string,
    productName: string,
    subName: string,
    placeHolderText: string,
    hva: HvaModel[],
    selectedStoreId: number,
    selectedSizes = new Set<string>(),
    maximumSizesSelectedError = false,
  ) {
    this.variantId = variantId;
    this.productName = productName;
    this.subName = subName;
    this.placeHolderText = placeHolderText;
    this.hva = hva;
    this.selectedStoreId = selectedStoreId;
    this.selectedSizes = selectedSizes;
    this.maximumSizesSelectedError = maximumSizesSelectedError;
  }

  public static create(variantId: string, hvaResponse: HvaResponse, config: Config) {
    const hva = hvaResponse.hva;
    const selectedStoreId = hva.length === 1 ? hva[0].haus.nummer : HvaModalModel.loadSelectedStoreId();
    return new HvaModalModel(
      variantId,
      hvaResponse.productName,
      hvaResponse.subName,
      config.messages.selectPlaceholder,
      hvaResponse.hva,
      selectedStoreId,
    );
  }

  private static loadSelectedStoreId() {
    const id = parseInt(window.localStorage.getItem('ents:hvaHausNummer') ?? '0', 10);
    return Number.isNaN(id) ? 0 : id;
  }

  public static empty() {
    return this.EMPTY;
  }

  public shouldReload(otherVariantId: string) {
    return this === HvaModalModel.EMPTY || this.variantId !== otherVariantId;
  }

  public applyOption(value: number) {
    const updated = this.update(value);
    updated.saveSelectedStoreId();
    return updated;
  }

  private update(
    updatedStoreId: number,
    updatedSelectedSizes = new Set<string>(),
    updatedMaximumSizesSelectedError = false,
  ) {
    return new HvaModalModel(
      this.variantId,
      this.productName,
      this.subName,
      this.placeHolderText,
      this.hva,
      updatedStoreId,
      updatedSelectedSizes,
      updatedMaximumSizesSelectedError,
    );
  }

  public selectedSizesCount() {
    return this.selectedSizes.size;
  }

  public toggleSizeSelection(id: string) {
    if (this.sizeSelected(id)) {
      return this.unselectSize(id);
    }
    return this.selectSize(id);
  }

  private selectSize(id: string) {
    if (!this.reservationEnabled()) {
      return this;
    }
    if (this.selectedSizesCount() === 3) {
      return this.update(this.selectedStoreId, this.selectedSizes, true);
    }
    this.selectedSizes.add(id);
    return this.update(this.selectedStoreId, this.selectedSizes);
  }

  private unselectSize(id: string) {
    this.selectedSizes.delete(id);
    return this.update(this.selectedStoreId, this.selectedSizes);
  }

  public sizeSelected(id: string) {
    return this.selectedSizes.has(id);
  }

  private saveSelectedStoreId() {
    window.localStorage.setItem('ents:hvaHausNummer', `${this.selectedStoreId}`);
  }

  public selectedHva(): HvaModel | undefined {
    const matchingHvaModels = this.hva.filter(entry => entry.haus.nummer === this.selectedStoreId);
    if (matchingHvaModels.length > 0) {
      return matchingHvaModels[0];
    }
    return undefined;
  }

  public reservationEnabled(): boolean {
    return this.selectedHva()?.haus?.onlinereservierungAktiv ?? false;
  }

  public reserve() {
    const payload = this.getPayload();
    const kaufReserveEvent = new CustomEvent('kaufen:reserve:add', payload);
    document.dispatchEvent(kaufReserveEvent);
    return payload;
  }

  private getPayload() {
    return {
      detail: {
        hausId: `${this.selectedHva()?.haus.nummer}`,
        artikelListe: [...this.selectedSizes.values()],
      },
    };
  }

  public getOptions(includePlaceHolder = true) {
    const hvaOptionModel = this.hva.map(hvaModel => new OptionModel(hvaModel.haus.name, hvaModel.haus.nummer));
    if (this.selectedStoreId === 0 && includePlaceHolder) {
      const placeHolder = new OptionModel(this.placeHolderText, 0);
      return [placeHolder, ...hvaOptionModel];
    }
    return hvaOptionModel;
  }

  public getSelectedOptionNameOrPlaceholder() {
    return this.selectedHva()?.haus?.name || this.placeHolderText;
  }

  public isOptionSelected(option: OptionModel) {
    return option.value === this.selectedStoreId;
  }
}

export class OptionModel {
  readonly name: string;

  readonly value: number;

  constructor(name: string, value: number) {
    this.name = name;
    this.value = value;
  }
}
